import { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, formatZAR } from "@/lib/api";
import {
  Download, FileText, Scale, AlertTriangle, RefreshCw,
  Handshake, Send, CheckCircle2, Copy, Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";

// MaintenanceDisclosurePanel — renders for maintenance_divorce matters only.
// Sections: (1) Financial Disclosure summary, (2) Doc-intelligence feedback,
// (3) Mutual Agreement builder (NEW — Phase 9.1).

export default function MaintenanceDisclosurePanel({ matterId, matter }) {
  const [disclosure, setDisclosure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const matterType = matter?.matter_type || matter?.service_type;
  const isVisible = matterType === "maintenance_divorce";

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/admin/matters/${matterId}/maintenance/disclosure`);
      setDisclosure(data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Failed to load disclosure");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && matterId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId, isVisible]);

  const downloadJ941 = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BACKEND_URL}/api/admin/matters/${matterId}/maintenance/j941.pdf`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || `HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `J941-${matter?.matter_reference || matterId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("J941 PDF downloaded");
    } catch (err) {
      toast.error(err.message || "PDF download failed");
    }
  };

  if (!isVisible) return null;

  return (
    <div className="border border-zinc-200 bg-white" data-testid="maintenance-disclosure-panel">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-amber-700" />
          <div>
            <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Maintenance · Financial Disclosure</div>
            <div className="text-sm text-zinc-900 font-medium mt-0.5">Indicative pro-rata calculation + J941 application</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading}
                     className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1 disabled:opacity-50"
                     data-testid="maintenance-refresh-btn">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button onClick={downloadJ941}
                     className="bg-zinc-900 text-white px-3 py-1.5 text-xs font-medium hover:bg-zinc-700 inline-flex items-center gap-1.5"
                     data-testid="download-j941-btn">
            <Download className="h-3 w-3" /> Download J941
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="mb-4 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 inline-block" data-testid="maintenance-disclaimer">
          {disclosure?.disclaimer || "This is an indicative assessment — court discretion applies."}
        </div>
        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {disclosure && (
          <div className="space-y-6">
            <SummaryRow disclosure={disclosure} />
            <BalanceSheet bs={disclosure.balance_sheet} />
            <ChildrenList childrenList={disclosure.children} />
            <DocIntelFeedback supportingDocuments={disclosure.supporting_documents} entities={matter?.entities} />
            <MutualAgreementSection matterId={matterId} matter={matter} disclosure={disclosure} />
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ disclosure }) {
  const calc = disclosure.calculation;
  const a = calc.parties.a;
  const b = calc.parties.b;
  return (
    <div data-testid="maintenance-calc-summary">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">Indicative monthly payment</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
        <Cell label="Total children's cost" value={formatZAR(calc.children_cost.monthly_total)} testid="cell-children-cost" />
        <Cell label={`${a.name} (${a.share_pct}%)`} value={formatZAR(a.contribution)} testid="cell-a-contribution" />
        <Cell label={`${b.name} (${b.share_pct}%)`} value={formatZAR(b.contribution)} testid="cell-b-contribution" />
        <Cell label="Indicative payment" value={formatZAR(calc.indicative_payment_monthly)} emphasis testid="cell-indicative-payment" />
      </div>
      <div className="text-xs text-zinc-500 mt-3" data-testid="maintenance-paying-party">
        Paying party: <span className="font-medium text-zinc-900">{calc.paying_party === "a" ? a.name : b.name}</span> · Receiving party:{" "}
        <span className="font-medium text-zinc-900">{calc.paying_party === "a" ? b.name : a.name}</span>
      </div>
      {calc.eligibility_flags?.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs">
          {calc.eligibility_flags.map((f) => (
            <li key={f.id} className={f.level === "warning" ? "text-amber-800" : "text-zinc-600"} data-testid={`flag-${f.id}`}>
              · {f.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BalanceSheet({ bs }) {
  if (!bs) return null;
  return (
    <div data-testid="maintenance-balance-sheet">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">Assets & liabilities</div>
      <div className="grid grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        {[bs.party_a, bs.party_b].map((p, idx) => (
          <div key={idx} className="bg-white p-4">
            <div className="text-xs font-medium text-zinc-900">{p.name}</div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-zinc-700">
              <div>Assets: <span className="font-medium">{formatZAR(p.assets_total)}</span></div>
              <div>Liab.: <span className="font-medium">{formatZAR(p.liabilities_total)}</span></div>
              <div className="col-span-2">Net worth:{" "}
                <span className={`font-medium ${p.net_worth < 0 ? "text-red-700" : "text-zinc-900"}`}>{formatZAR(p.net_worth)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChildrenList({ childrenList }) {
  if (!childrenList?.length) return null;
  return (
    <div data-testid="maintenance-children-list">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">Children</div>
      <table className="w-full text-sm border border-zinc-200">
        <thead className="bg-zinc-50 text-xs text-zinc-500 uppercase tracking-[0.18em]">
          <tr>
            <th className="text-left px-3 py-2">Name</th>
            <th className="text-left px-3 py-2">Age</th>
            <th className="text-left px-3 py-2">School</th>
            <th className="text-left px-3 py-2">Primary caregiver</th>
          </tr>
        </thead>
        <tbody>
          {childrenList.map((c, i) => (
            <tr key={i} className="border-t border-zinc-100" data-testid={`child-disclosure-row-${i}`}>
              <td className="px-3 py-2">{c.name || `Child ${i + 1}`}</td>
              <td className="px-3 py-2">{c.age || "—"}</td>
              <td className="px-3 py-2">{c.school || "—"}</td>
              <td className="px-3 py-2">{c.primary_caregiver || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DocIntelFeedback — shows which fields were extracted from documents and
// which need attention (low confidence / missing).
// ---------------------------------------------------------------------------

function DocIntelFeedback({ supportingDocuments, entities }) {
  const docs = supportingDocuments || [];
  if (!docs.length) {
    return (
      <div className="border border-dashed border-zinc-200 p-4 text-xs text-zinc-500" data-testid="doc-intel-feedback-empty">
        No documents extracted yet. Upload payslips / bank statements / expense docs in the Legal Requirements portal — extracted fields will appear here after admin acceptance.
      </div>
    );
  }
  return (
    <div data-testid="doc-intel-feedback">
      <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-3">Document Intelligence — extracted fields</div>
      <div className="space-y-2">
        {docs.map((d, idx) => {
          const fields = d.fields || {};
          const populated = Object.entries(fields).filter(([, v]) => v !== null && v !== undefined && v !== "");
          const isLowConf = (d.fields?.confidence === "low") || populated.length < 2;
          return (
            <div key={idx} className="border border-zinc-200 p-3" data-testid={`doc-intel-row-${idx}`}>
              <div className="flex items-center justify-between text-xs">
                <div className="font-medium text-zinc-800 inline-flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5 text-zinc-500" />
                  {(d.doc_type || "document").replace(/_/g, " ")} · for {d.party === "party_b" ? "Other party" : "Applicant"}
                </div>
                {isLowConf
                  ? <span className="text-[10px] tracking-[0.14em] uppercase bg-amber-50 text-amber-700 px-1.5 py-0.5">Review</span>
                  : <span className="text-[10px] tracking-[0.14em] uppercase bg-emerald-50 text-emerald-700 px-1.5 py-0.5">Extracted</span>
                }
              </div>
              <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-zinc-700">
                {populated.length === 0
                  ? <li className="col-span-2 text-amber-700">No fields extracted — re-upload a higher-quality document.</li>
                  : populated.map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-3" data-testid={`doc-intel-field-${idx}-${k}`}>
                      <span className="text-zinc-500">{k.replace(/_/g, " ")}</span>
                      <span className="font-medium text-zinc-900 truncate text-right">{typeof v === "number" ? formatZAR(v) : String(v)}</span>
                    </li>
                  ))
                }
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MutualAgreementSection — Phase 9.1
// ---------------------------------------------------------------------------

function MutualAgreementSection({ matterId, matter, disclosure }) {
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const calc = disclosure?.calculation;
  const a = calc?.parties?.a;
  const b = calc?.parties?.b;
  const defaultTerms = useMemo(() => ({
    party_a_name:    a?.name || "",
    party_b_name:    b?.name || "",
    paying_party:    calc?.paying_party || "a",
    monthly_amount:  calc?.indicative_payment_monthly || 0,
    payment_due_day: 1,
    start_date:      new Date().toISOString().slice(0, 10),
    escalation_pct:  5,
    duration:        "until_majority",
    fixed_term_end:  "",
    payment_method:  "eft",
    additional_terms: "",
    children: (disclosure?.children || []).map((c) => ({ name: c.name, age: c.age })),
  }), [calc, disclosure, a, b]);
  const [terms, setTerms] = useState(defaultTerms);

  const fetchAgreement = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/matters/${matterId}/maintenance/agreement`);
      setAgreement(data);
      setTerms({ ...defaultTerms, ...(data.terms || {}), children: (data.terms?.children || defaultTerms.children) });
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.detail || err.message);
      }
      setAgreement(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (matterId) fetchAgreement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterId]);

  useEffect(() => {
    if (!agreement) setTerms(defaultTerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTerms]);

  const createAgreement = async () => {
    if (!terms.party_a_name || !terms.party_b_name || !terms.monthly_amount) {
      toast.error("Need both party names + a monthly amount.");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post(`/admin/matters/${matterId}/maintenance/agreement`, {
        terms: {
          ...terms,
          monthly_amount: Number(terms.monthly_amount),
          payment_due_day: Number(terms.payment_due_day),
          escalation_pct: Number(terms.escalation_pct) || 0,
        },
      });
      setAgreement(data);
      toast.success("Agreement created — share the links below.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setCreating(false);
    }
  };

  const downloadAgreementPdf = async () => {
    try {
      const token = localStorage.getItem("token");
      const resp = await fetch(`${BACKEND_URL}/api/admin/matters/${matterId}/maintenance/agreement.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a2 = document.createElement("a");
      a2.href = url; a2.download = `Maintenance-Agreement-${matter?.matter_reference || matterId}.pdf`;
      document.body.appendChild(a2); a2.click(); document.body.removeChild(a2);
      window.URL.revokeObjectURL(url);
      toast.success("Agreement PDF downloaded");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/agreement/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="border border-zinc-200 p-5 bg-zinc-50/40" data-testid="mutual-agreement-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Handshake className="h-4 w-4 text-emerald-700" />
          <div>
            <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Mutual Agreement Mode</div>
            <div className="text-sm font-medium text-zinc-900">Two-party confirmation flow</div>
          </div>
        </div>
        {agreement && (
          <span className={`text-[10px] tracking-[0.14em] uppercase px-2 py-1 ${agreement.status === "fully_confirmed" ? "bg-emerald-50 text-emerald-700" : agreement.status === "confirmed_by_one" ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-700"}`} data-testid="agreement-status-badge">
            {agreement.status.replace(/_/g, " ")}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-zinc-500">Loading agreement…</div>
      ) : !agreement ? (
        <AgreementBuilder terms={terms} setTerms={setTerms} onCreate={createAgreement} creating={creating} />
      ) : (
        <ExistingAgreementView agreement={agreement} onCopy={copyLink} onDownloadPdf={downloadAgreementPdf} onReplace={() => setAgreement(null)} />
      )}

      <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="mutual-agreement-court-order-note">
        This agreement must be made an order of court to be enforceable.
      </div>
    </div>
  );
}

function AgreementBuilder({ terms, setTerms, onCreate, creating }) {
  const update = (k, v) => setTerms((t) => ({ ...t, [k]: v }));
  return (
    <div className="space-y-3" data-testid="agreement-builder-form">
      <div className="grid grid-cols-2 gap-2 text-xs">
        <BInput label="Party A name" value={terms.party_a_name} onChange={(v) => update("party_a_name", v)} testid="builder-party-a-name" />
        <BInput label="Party B name" value={terms.party_b_name} onChange={(v) => update("party_b_name", v)} testid="builder-party-b-name" />
        <BSelect label="Paying party" value={terms.paying_party} onChange={(v) => update("paying_party", v)} options={[{ value: "a", label: terms.party_a_name || "Party A" }, { value: "b", label: terms.party_b_name || "Party B" }]} testid="builder-paying-party" />
        <BInput label="Monthly amount (ZAR)" type="number" value={terms.monthly_amount} onChange={(v) => update("monthly_amount", v)} testid="builder-monthly-amount" />
        <BInput label="Payment day (1–28)" type="number" value={terms.payment_due_day} onChange={(v) => update("payment_due_day", v)} testid="builder-payment-day" />
        <BInput label="Start date" type="date" value={terms.start_date} onChange={(v) => update("start_date", v)} testid="builder-start-date" />
        <BInput label="Escalation % p.a." type="number" value={terms.escalation_pct} onChange={(v) => update("escalation_pct", v)} testid="builder-escalation" />
        <BSelect label="Duration" value={terms.duration} onChange={(v) => update("duration", v)} options={[
          { value: "until_majority", label: "Until each child attains majority" },
          { value: "fixed_term",     label: "Fixed term (specify end date)" },
          { value: "indefinite",      label: "Indefinite (variable on agreement / court)" },
        ]} testid="builder-duration" />
        {terms.duration === "fixed_term" && (
          <BInput label="Fixed-term end date" type="date" value={terms.fixed_term_end} onChange={(v) => update("fixed_term_end", v)} testid="builder-fixed-term-end" />
        )}
        <BSelect label="Payment method" value={terms.payment_method} onChange={(v) => update("payment_method", v)} options={[
          { value: "eft",         label: "EFT" },
          { value: "debit_order", label: "Debit order" },
          { value: "cash",        label: "Cash" },
          { value: "other",       label: "Other" },
        ]} testid="builder-payment-method" />
      </div>
      <BInput label="Additional terms (free text)" type="textarea" value={terms.additional_terms} onChange={(v) => update("additional_terms", v)} testid="builder-additional-terms" />
      <button onClick={onCreate} disabled={creating} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-700 disabled:opacity-50" data-testid="builder-create-agreement-btn">
        <Send className="h-4 w-4" /> {creating ? "Creating…" : "Create agreement & generate two-party links"}
      </button>
    </div>
  );
}

function ExistingAgreementView({ agreement, onCopy, onDownloadPdf, onReplace }) {
  const terms = agreement.terms || {};
  const confirmations = agreement.confirmations || {};
  return (
    <div className="space-y-4" data-testid="existing-agreement-view">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
        <PartyConfirmation party="a" name={terms.party_a_name} token={agreement.party_a_token} confirmation={confirmations.party_a} onCopy={onCopy} />
        <PartyConfirmation party="b" name={terms.party_b_name} token={agreement.party_b_token} confirmation={confirmations.party_b} onCopy={onCopy} />
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={onDownloadPdf} className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-1.5 text-xs font-medium text-zinc-800 hover:border-zinc-500" data-testid="download-agreement-pdf-btn">
          <Download className="h-3 w-3" /> Download agreement PDF
        </button>
        <button onClick={onReplace} className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white px-3 py-1.5 text-xs text-zinc-600 hover:border-zinc-500" data-testid="replace-agreement-btn">
          <RefreshCw className="h-3 w-3" /> Replace with new draft
        </button>
      </div>
    </div>
  );
}

function PartyConfirmation({ party, name, token, confirmation, onCopy }) {
  const confirmed = !!confirmation?.confirmed_at;
  return (
    <div className={`p-3 ${confirmed ? "bg-emerald-50" : "bg-white"}`} data-testid={`agreement-party-${party}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{name || `Party ${party.toUpperCase()}`}</div>
          <div className="text-sm font-medium mt-1 flex items-center gap-1.5">
            {confirmed ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <span className="h-2 w-2 rounded-full bg-amber-400" />}
            {confirmed ? "Confirmed" : "Pending confirmation"}
          </div>
          {confirmed && <div className="text-[10px] text-zinc-500 mt-0.5">{new Date(confirmation.confirmed_at).toLocaleString()}</div>}
        </div>
        <button onClick={() => onCopy(token)} className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1" data-testid={`copy-link-${party}`}>
          <LinkIcon className="h-3 w-3" /> Copy link
        </button>
      </div>
    </div>
  );
}

function BInput({ label, value, onChange, type = "text", testid }) {
  if (type === "textarea") {
    return (
      <label className="block col-span-2">
        <span className="block text-xs text-zinc-500 mb-1">{label}</span>
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
                     data-testid={testid}
                     className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white" />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white" />
    </label>
  );
}

function BSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-zinc-500 mb-1">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                 className="w-full border border-zinc-300 px-2 py-1.5 text-sm bg-white">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Cell({ label, value, emphasis, testid }) {
  return (
    <div className={`p-4 ${emphasis ? "bg-zinc-900 text-white" : "bg-white"}`} data-testid={testid}>
      <div className={`text-xs uppercase tracking-[0.18em] ${emphasis ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
      <div className={`mt-1 font-serif text-xl ${emphasis ? "text-white" : "text-zinc-900"}`}>{value}</div>
    </div>
  );
}
